<main class="text-start">
  <h1 class="page-title">{{ 'wallet.title' | translate }}</h1>

  <div class="w-full bg-[#F6F6F6] rounded-lg p-3.5 space-y-3.5">

    <!-- Balance overview cards -->
    <div class="w-full grid md:grid-cols-2 xl:grid-cols-4 gap-4">
      <div class="bg-white w-full rounded-xl h-full p-4 py-5">
        <app-overview-card
        [title]="'wallet.cards.total_sales' | translate"
        [number]="wallet?.total_sales"
        [loading]="!wallet"
        [showCurrency]="true"
        iconClass="bi bi-graph-up-arrow"
        iconColor="#0768F7"
        iconBgColor="#DCEAFC">
      </app-overview-card>
    </div>
    <div class="bg-white w-full rounded-xl h-full p-4 py-5">
      <app-overview-card
        [title]="'wallet.cards.total_payouts' | translate"
        [number]="wallet?.total_payouts"
        [loading]="!wallet"
        [showCurrency]="true"
        iconClass="bi bi-arrow-left-right"
        iconColor="#4AD991"
        iconBgColor="#DCFCE7">
      </app-overview-card>
    </div>
    <div class="bg-white w-full rounded-xl h-full px-4 py-5">
      <app-overview-card
        [title]="'wallet.cards.available_balance' | translate"
        [number]="wallet?.available_balance"
        [loading]="!wallet"
        [showCurrency]="true"
        iconClass="bi bi-wallet2"
        iconColor="#8280FF"
        iconBgColor="#E4E4FF">
      </app-overview-card>
    </div>
    <div class="bg-white w-full rounded-xl h-full p-4 py-5">
      <app-overview-card
        [title]="'wallet.cards.pending_balance' | translate"
        [number]="wallet?.pending_balance"
        [loading]="!wallet"
        [showCurrency]="true"
        iconClass="bi bi-hourglass-split"
        iconColor="#FEC53D"
        iconBgColor="#FEF2D6">
      </app-overview-card>
    </div>
    </div>

    <!-- Transactions table -->
    <div class="w-full bg-white rounded-xl px-5 py-5">
      <div class="flex flex-col md:flex-row justify-between md:items-center gap-3 mb-3">
        <p class="section-title whitespace-nowrap">{{ 'wallet.transactions.section_title' | translate }}</p>
        <div class="max-sm:w-full flex flex-col sm:flex-row sm:items-center gap-3">
          <div class="border border-[#e0e0e0] md:w-52 sm:w-72 h-8 rounded-lg overflow-hidden flex items-center">
            <span class="bg-white ps-2.5 h-full flex items-center">
              <i class="bi bi-search text-sm"></i>
            </span>
            <input type="text"
              class="bg-white px-4 w-full h-full text-start focus:outline-none focus:border-black text-xs"
              [placeholder]="'wallet.transactions.search_placeholder' | translate" (input)="onTransactionsSearchChange($event)">
          </div>
          <div class="flex items-center gap-4">
            <app-filters-dropdown
              [filters]="transactionsFilters"
              [ngModel]="transactionsFilterValues"
              (ngModelChange)="onTransactionsFiltersChange($event)"
              [lang]="languageService.currentLang()"
            />
            <button app-export-excel-button class="hidden sm:inline-flex"
              [disabled]="isExportingTransactions"
              (click)="onExportTransactionsExcel()"></button>
          </div>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="generic-table">
          <thead>
            <tr>
              <th>{{ 'wallet.transactions.table.uuid' | translate }}</th>
              <th>{{ 'wallet.transactions.table.kind' | translate }}</th>
              <th>{{ 'wallet.transactions.table.amount' | translate }}</th>
              <th>{{ 'wallet.transactions.table.pending_balance' | translate }}</th>
              <th>{{ 'wallet.transactions.table.available_balance' | translate }}</th>
              <th>{{ 'wallet.transactions.table.date' | translate }}</th>
              <th>{{ 'wallet.transactions.table.note' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngIf="!transactionsResponse">
              <tr *ngFor="let _ of [1,2,3,4,5]" app-table-skeleton-row [columns]="7"></tr>
            </ng-container>
            <tr *ngFor="let transaction of transactionsResponse?.results">
              <td class="text-gray-600">#{{ transaction.uuid | slice:0:8 }}</td>
              <td>
                <span [ngClass]="{
                    'bg-yellow-100 text-yellow-700': transaction.kind === 'pending_credit',
                    'bg-blue-100 text-blue-700': transaction.kind === 'pending_release',
                    'bg-emerald-100 text-emerald-700': transaction.kind === 'pending_settle',
                    'bg-red-100 text-red-700': transaction.kind === 'available_reversal',
                    'bg-purple-100 text-purple-700': transaction.kind === 'payout'
                  }" class="status-chip">
                  {{ 'wallet.transactions.kinds.' + transaction.kind | translate }}
                </span>
              </td>
              <td>{{ transaction.amount }} {{ 'common.currency' | translate }}</td>
              <td>{{ transaction.pending_balance_after }} {{ 'common.currency' | translate }}</td>
              <td>{{ transaction.available_balance_after }} {{ 'common.currency' | translate }}</td>
              <td>{{ transaction.created_at | date: 'dd MMM / hh:mm a' }}</td>
              <td>{{ transaction.note || '—' }}</td>
            </tr>
            <tr *ngIf="transactionsResponse?.count == 0">
              <td colspan="100%" class="text-start">{{ 'wallet.transactions.empty' | translate }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <app-table-pagination
        *ngIf="transactionsResponse"
        [count]="transactionsResponse.count"
        [limit]="transactionsPagination.limit"
        [offset]="transactionsPagination.offset"
        (pageChange)="onTransactionsPageChange($event)" />
    </div>

    <!-- Payouts table -->
    <div class="w-full bg-white rounded-xl px-5 py-5">
      <div class="flex flex-col md:flex-row justify-between md:items-center gap-3 mb-3">
        <p class="section-title whitespace-nowrap">{{ 'wallet.payouts.section_title' | translate }}</p>
        <div class="max-sm:w-full flex flex-col sm:flex-row sm:items-center gap-3">
          <div class="border border-[#e0e0e0] md:w-52 sm:w-72 h-8 rounded-lg overflow-hidden flex items-center">
            <span class="bg-white ps-2.5 h-full flex items-center">
              <i class="bi bi-search text-sm"></i>
            </span>
            <input type="text"
              class="bg-white px-4 w-full h-full text-start focus:outline-none focus:border-black text-xs"
              [placeholder]="'wallet.payouts.search_placeholder' | translate" (input)="onPayoutsSearchChange($event)">
          </div>
          <div class="flex items-center gap-4">
            <app-filters-dropdown
              [filters]="payoutsFilters"
              [ngModel]="payoutsFilterValues"
              (ngModelChange)="onPayoutsFiltersChange($event)"
              [lang]="languageService.currentLang()"
            />
            <button app-export-excel-button class="hidden sm:inline-flex"
              [disabled]="isExportingPayouts"
              (click)="onExportPayoutsExcel()"></button>
          </div>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="generic-table">
          <thead>
            <tr>
              <th>{{ 'wallet.payouts.table.uuid' | translate }}</th>
              <th>{{ 'wallet.payouts.table.reference' | translate }}</th>
              <th>{{ 'wallet.payouts.table.amount' | translate }}</th>
              <th>{{ 'wallet.payouts.table.method' | translate }}</th>
              <th>{{ 'wallet.payouts.table.status' | translate }}</th>
              <th>{{ 'wallet.payouts.table.note' | translate }}</th>
              <th>{{ 'wallet.payouts.table.date' | translate }}</th>
              <th>{{ 'wallet.payouts.table.attachments' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngIf="!payoutsResponse">
              <tr *ngFor="let _ of [1,2,3,4,5]" app-table-skeleton-row [columns]="8"></tr>
            </ng-container>
            <tr *ngFor="let payout of payoutsResponse?.results">
              <td class="text-gray-600">#{{ payout.uuid | slice:0:8 }}</td>
              <td>{{ payout.reference || '—' }}</td>
              <td>{{ payout.amount }} {{ 'common.currency' | translate }}</td>
              <td>{{ 'wallet.payouts.methods.' + payout.method | translate }}</td>
              <td>
                <span [ngClass]="{
                    'bg-yellow-100 text-yellow-700': payout.status === 'created',
                    'bg-green-100 text-green-700': payout.status === 'completed',
                    'bg-red-100 text-red-700': payout.status === 'failed',
                    'bg-gray-100 text-gray-700': payout.status === 'reversed'
                  }" class="status-chip">
                  {{ 'wallet.payouts.statuses.' + payout.status | translate }}
                </span>
              </td>
              <td>{{ payout.notes || '—' }}</td>
              <td>{{ payout.created_at | date: 'dd MMM / hh:mm a' }}</td>
              <td>
                <a *ngIf="payout.attachment" [href]="payout.attachment" target="_blank"
                  class="text-[#121217] bg-white px-3 py-1 text-xs border rounded-md border-[#D1D1DB] cursor-pointer">{{ 'wallet.payouts.view_attachment' | translate }}</a>
                <span *ngIf="!payout.attachment">—</span>
              </td>
            </tr>
            <tr *ngIf="payoutsResponse?.count == 0">
              <td colspan="100%" class="text-start">{{ 'wallet.payouts.empty' | translate }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <app-table-pagination
        *ngIf="payoutsResponse"
        [count]="payoutsResponse.count"
        [limit]="payoutsPagination.limit"
        [offset]="payoutsPagination.offset"
        (pageChange)="onPayoutsPageChange($event)" />
    </div>

  </div>
</main>
